import { holdingsForState } from '../../domain/holdings';
import type {
  AtoReport,
  ChangeRequest,
  MigrationJob,
  RegistryState,
  TaxStatementBatch,
} from '../../data/types';
import { newId } from '../../lib/random';
import type { RegistryAction } from '../actions';
import {
  addApproval,
  addCommunication,
  addDocuments,
  fundCode,
  holderName,
  today,
  updateHolder,
  withAudit,
} from '../helpers';

type Handler<T extends RegistryAction['type']> = (
  state: RegistryState,
  action: Extract<RegistryAction, { type: T }>,
) => RegistryState;

export const updateKyc: Handler<'KYC_UPDATE'> = (state, action) => {
  const kycCase = state.kycCases.find((item) => item.id === action.id);
  if (!kycCase) return state;
  let next: RegistryState = {
    ...state,
    kycCases: state.kycCases.map((item) =>
      item.id === action.id
        ? {
            ...item,
            status: action.status,
            resolution: action.resolution ?? item.resolution,
            assignedTo: action.assignedTo ?? item.assignedTo,
          }
        : item,
    ),
  };
  if (action.status === 'CLEARED' && kycCase.holderId) {
    next = updateHolder(next, kycCase.holderId, (holder) =>
      holder.kycStatus === 'REFER' || holder.kycStatus === 'EXPIRED'
        ? { kycStatus: 'VERIFIED', kycVerifiedAt: today, kycRefreshDue: '2029-10-16' }
        : {},
    );
  }
  if (action.status === 'CLEARED' && kycCase.applicationId) {
    next = {
      ...next,
      applications: next.applications.map((application) =>
        application.id === kycCase.applicationId
          ? {
              ...application,
              status: application.status === 'KYC_REVIEW' ? 'AWAITING_FUNDS' : application.status,
              checklist: { ...application.checklist, identity: 'DONE', owners: 'DONE' },
            }
          : application,
      ),
    };
  }
  return withAudit(
    next,
    `KYC case ${action.status.toLowerCase().replace('_', ' ')}`,
    kycCase.subjectName,
    action.resolution ?? kycCase.type,
  );
};

const CHANGE_NEEDS_APPROVAL: ChangeRequest['kind'][] = ['BANK_CHANGE', 'TRANSFER', 'REDEMPTION'];

export const advanceChangeRequest: Handler<'CHANGE_REQUEST_ADVANCE'> = (state, action) => {
  const request = state.changeRequests.find((item) => item.id === action.id);
  if (!request) return state;
  let next: RegistryState = {
    ...state,
    changeRequests: state.changeRequests.map((item) =>
      item.id === action.id ? { ...item, status: action.status } : item,
    ),
  };
  if (action.status === 'PENDING_APPROVAL' && CHANGE_NEEDS_APPROVAL.includes(request.kind)) {
    next = addApproval(next, {
      kind:
        request.kind === 'BANK_CHANGE'
          ? 'BANK_CHANGE'
          : request.kind === 'TRANSFER'
            ? 'TRANSFER'
            : 'REDEMPTION',
      refId: request.id,
      fundId: request.fundId,
      title: `${request.kind === 'BANK_CHANGE' ? 'Bank account change' : request.kind === 'TRANSFER' ? 'Unit transfer' : 'Redemption'}: ${holderName(state, request.holderId)}`,
      summary: request.details.map((detail) => `${detail.label}: ${detail.value}`).join(' · '),
      changes:
        request.kind === 'BANK_CHANGE'
          ? request.details.map((detail) => ({
              field: detail.label,
              from: '(current)',
              to: detail.value,
            }))
          : undefined,
    });
  }
  if (action.status === 'QUEUED' && request.kind === 'REDEMPTION') {
    next = {
      ...next,
      redemptionRuns: next.redemptionRuns.map((run) =>
        run.status === 'OPEN' &&
        run.classId === request.classId &&
        !run.lines.some((line) => line.requestId === request.id)
          ? {
              ...run,
              lines: [
                ...run.lines,
                {
                  holderId: request.holderId,
                  requestId: request.id,
                  unitsRequested: request.units ?? 0,
                  unitsRedeemed: 0,
                  amount: 0,
                },
              ],
            }
          : run,
      ),
    };
  }
  if (action.status === 'COMPLETED' && request.kind === 'DRP_ELECTION') {
    next = updateHolder(next, request.holderId, (holder) => ({ drpElection: !holder.drpElection }));
  }
  return withAudit(
    next,
    `Change request ${action.status.toLowerCase().replace('_', ' ')}`,
    request.reference,
    holderName(state, request.holderId),
  );
};

export const createChangeRequest: Handler<'CHANGE_REQUEST_CREATE'> = (state, action) => {
  const request: ChangeRequest = {
    ...action.request,
    id: newId('cr'),
    reference: `CR-26-${String(400 + state.changeRequests.length).padStart(4, '0')}`,
    status: 'SUBMITTED',
  };
  return withAudit(
    { ...state, changeRequests: [request, ...state.changeRequests] },
    'Change request submitted',
    request.reference,
    `${request.kind} from ${holderName(state, request.holderId)}`,
    request.channel === 'INVESTOR_PORTAL' ? 'Investor portal' : undefined,
  );
};

const TAX_FLOW: TaxStatementBatch['status'][] = [
  'NOT_STARTED',
  'COMPONENTS_IMPORTED',
  'GENERATED',
  'REVIEWED',
  'PUBLISHED',
];

export const advanceTaxBatch: Handler<'TAX_BATCH_ADVANCE'> = (state, action) => {
  const batch = state.taxBatches.find((item) => item.id === action.id);
  if (!batch || batch.status === 'PUBLISHED') return state;
  const status = TAX_FLOW[TAX_FLOW.indexOf(batch.status) + 1];
  const holderIds = Array.from(
    new Set(
      holdingsForState(state, today)
        .filter((holding) => holding.fundId === batch.fundId)
        .map((holding) => holding.holderId),
    ),
  );
  let next: RegistryState = {
    ...state,
    taxBatches: state.taxBatches.map((item) =>
      item.id === action.id
        ? {
            ...item,
            status,
            statementCount: status === 'GENERATED' ? holderIds.length : item.statementCount,
            componentsSource:
              status === 'COMPONENTS_IMPORTED'
                ? 'Fund accountant workbook (Xero export)'
                : item.componentsSource,
            publishedAt: status === 'PUBLISHED' ? today : item.publishedAt,
          }
        : item,
    ),
  };
  if (status === 'PUBLISHED') {
    next = addDocuments(
      next,
      holderIds.map((holderId) => ({
        holderId,
        fundId: batch.fundId,
        kind: 'TAX_STATEMENT' as const,
        refId: batch.financialYear,
        name: `${fundCode(state, batch.fundId)} ${batch.financialYear} AMMA tax statement`,
        date: today,
        sizeKb: 140,
        visibleToInvestor: true,
      })),
    );
    next = addCommunication(next, {
      template: 'TAX_STATEMENT_READY',
      subject: `Your ${fundCode(state, batch.fundId)} ${batch.financialYear} tax statement is ready`,
      audience: `${fundCode(state, batch.fundId)} holders`,
      fundId: batch.fundId,
      holderIds,
      channels: ['EMAIL', 'PUSH'],
    });
  }
  return withAudit(
    next,
    `Tax statements: ${status.toLowerCase().replace('_', ' ')}`,
    `${fundCode(state, batch.fundId)} ${batch.financialYear}`,
    `${holderIds.length} holders`,
  );
};

export const createTaxBatch: Handler<'TAX_BATCH_CREATE'> = (state, action) => {
  if (
    state.taxBatches.some(
      (item) => item.fundId === action.fundId && item.financialYear === action.financialYear,
    )
  )
    return state;
  const batch: TaxStatementBatch = {
    id: newId('tb'),
    fundId: action.fundId,
    financialYear: action.financialYear,
    status: 'NOT_STARTED',
    statementCount: 0,
  };
  return { ...state, taxBatches: [batch, ...state.taxBatches] };
};

const ATO_FLOW: AtoReport['status'][] = ['NOT_STARTED', 'DRAFT', 'VALIDATED', 'LODGED'];
const AIIR_FLOW: AtoReport['status'][] = [
  'NOT_STARTED',
  'DRAFT',
  'IN_REVIEW',
  'VALIDATED',
  'LODGED',
];

export const advanceAtoReport: Handler<'ATO_REPORT_ADVANCE'> = (state, action) => {
  const report = state.atoReports.find((item) => item.id === action.id);
  if (!report || report.status === 'LODGED') return state;
  const flow = report.kind === 'AIIR' ? AIIR_FLOW : ATO_FLOW;
  const status = flow[flow.indexOf(report.status) + 1];
  const next: RegistryState = {
    ...state,
    atoReports: state.atoReports.map((item) =>
      item.id === action.id
        ? {
            ...item,
            status,
            issues: status === 'VALIDATED' ? 0 : item.issues,
            lodgedAt: status === 'LODGED' ? today : item.lodgedAt,
            receipt:
              status === 'LODGED'
                ? `ATO-${item.kind}-${Date.now().toString().slice(-8)}`
                : item.receipt,
          }
        : item,
    ),
  };
  return withAudit(
    next,
    `${report.kind} ${status.toLowerCase().replace('_', ' ')}`,
    report.period,
    `${report.records} records`,
  );
};

const MIGRATION_FLOW: MigrationJob['status'][] = [
  'UPLOADED',
  'MAPPED',
  'MATCHED',
  'RECONCILED',
  'COMMITTED',
];

export const createMigration: Handler<'MIGRATION_CREATE'> = (state, action) => {
  const job: MigrationJob = {
    id: newId('mg'),
    fundId: action.fundId,
    source: action.source,
    status: 'UPLOADED',
    fileName: action.fileName,
    rows: action.rows,
    matched: 0,
    newHolders: 0,
    exceptions: 0,
    unitsVariance: 0,
    createdAt: today,
  };
  return withAudit(
    { ...state, migrations: [job, ...state.migrations] },
    'Migration file uploaded',
    action.fileName,
    `${action.rows} rows from ${action.source}`,
  );
};

export const advanceMigration: Handler<'MIGRATION_ADVANCE'> = (state, action) => {
  const job = state.migrations.find((item) => item.id === action.id);
  if (!job || job.status === 'COMMITTED' || job.status === 'RECONCILED') {
    if (
      job?.status === 'RECONCILED' &&
      !state.approvals.some((item) => item.refId === job.id && item.status === 'PENDING')
    ) {
      return addApproval(state, {
        kind: 'MIGRATION',
        refId: job.id,
        fundId: job.fundId,
        title: `Commit migration: ${fundCode(state, job.fundId)} from ${job.source}`,
        summary: `${job.rows} holdings, ${job.exceptions} exceptions, units variance ${job.unitsVariance}.`,
      });
    }
    return state;
  }
  const status = MIGRATION_FLOW[MIGRATION_FLOW.indexOf(job.status) + 1];
  const matched = status === 'MATCHED' ? Math.max(0, job.rows - 3) : job.matched;
  const next: RegistryState = {
    ...state,
    migrations: state.migrations.map((item) =>
      item.id === action.id
        ? {
            ...item,
            status,
            matched,
            newHolders: status === 'MATCHED' ? Math.min(2, job.rows) : item.newHolders,
            exceptions: status === 'MATCHED' ? 1 : status === 'RECONCILED' ? 0 : item.exceptions,
          }
        : item,
    ),
  };
  return withAudit(
    next,
    `Migration ${status.toLowerCase()}`,
    job.fileName,
    fundCode(state, job.fundId),
  );
};

export const updateHolderRecord: Handler<'HOLDER_UPDATE'> = (state, action) => {
  const next = updateHolder(state, action.id, () => action.patch);
  return withAudit(next, 'Holder record updated', holderName(state, action.id), action.reason);
};
